import { createRouter, createWebHistory } from "vue-router";
import { useUserStore } from "@/stores/user";
import type { NavigationGuardNext } from "vue-router";
import type { MiddlewareContext } from "@/types";

import authRoutes from "@/views/auth/authRoutes";
import inviteRoutes from "@/views/invite/inviteRoutes";
import signInRoutes from "@/views/signIn/signInRoutes";
import signOutRoutes from "@/views/signOut/signOutRoutes";
import helpRoutes from "@/views/help/helpRoutes";
import homeRoutes from "@/views/home/homeRoutes";
import enrollmentRoutes from "@/views/enrollment/enrollmentRoutes";
import groupRoutes from "@/views/group/groupRoutes";
import catalogRoutes from "@/views/catalog/catalogRoutes";
import cartRoutes from "@/views/cart/cartRoutes";
import checkoutRoutes from "@/views/checkout/checkoutRoutes";
import orderRoutes from "@/views/orders/orderRoutes";
import notFoundRoutes from "@/views/notFound/notFoundRoutes";
import errorRoutes from "@/views/error/errorRoutes";
import coursesRoutes from "@/views/courses/coursesRoutes";
import gradebookRoutes from "@/views/gradebook/gradebookRoutes";
import internalRoutes from "@/views/internal/internalRoutes";
import organizationRoutes from "@/views/organization/organizationRoutes";
import userRoutes from "@/views/users/userRoutes";

export const routes = [
  ...authRoutes,
  ...inviteRoutes,
  ...signInRoutes,
  ...signOutRoutes,
  ...helpRoutes,
  ...homeRoutes,
  ...enrollmentRoutes,
  ...groupRoutes,
  ...catalogRoutes,
  ...cartRoutes,
  ...checkoutRoutes,
  ...orderRoutes,
  ...gradebookRoutes,
  ...notFoundRoutes,
  ...errorRoutes,
  ...coursesRoutes,
  ...organizationRoutes,
  ...internalRoutes,
  ...userRoutes
];

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_APP_BASE_PATH),
  routes,
  scrollBehavior(to) {
    // Scroll to anchor
    if (to.query?.scrollTo || to.hash) {
      return {
        el: to.hash,
        behavior: "smooth"
      };
    } else {
      // Always scroll to top
      return { top: 0 };
    }
  }
});

router.onError((error, to) => {
  if (error.message.includes("Failed to fetch dynamically imported module")) {
    window.location.href = to.fullPath;
  }
});

// Router Middleware
const nextFactory = (context: MiddlewareContext, middleware: any, index: number) => {
  const subsequentMiddleware = middleware[index];

  if (!subsequentMiddleware) {
    return context.next;
  }

  return () => {
    const nextMiddleware: NavigationGuardNext = nextFactory(context, middleware, index + 1);
    subsequentMiddleware({ ...context, next: nextMiddleware });
  };
};

router.beforeEach((to, from, next) => {
  if (to.meta.middleware) {
    const middleware: Array<(context: MiddlewareContext) => void> = Array.isArray(
      to.meta.middleware
    )
      ? to.meta.middleware
      : [to.meta.middleware];

    const context: MiddlewareContext = {
      to,
      from,
      next,
      router
    };

    const nextMiddleware = nextFactory(context, middleware, 1);

    return middleware[0]({ ...context, next: nextMiddleware });
  }

  return next();
});

router.beforeEach((to: any) => {
  const store = useUserStore();

  // If any url contains a referralCode, save it to a cookie
  if (to.query.referralCode) {
    const parts = location.hostname.split(".");
    const domain = parts.slice(-2).join(".");
    document.cookie =
      "referralCode=" + String(to.query.referralCode) + ";domain=." + domain + ";path=/";
    delete to.query.referralCode;
    router.push({ path: to.fullPath, query: to.query });
  }

  // Redirect to activation page
  const isActivationRequired =
    to.name !== "ActivateAccount" &&
    store.isActivationRequired &&
    to.name !== "SignOut" &&
    to.name !== "SignIn" &&
    to.name !== "Error";
  if (isActivationRequired) {
    return {
      path: `activate-account/${store.currentUser?.activation_required}`,
      // Save the location we were at to come back later
      query: { redirect: to.fullPath.replace("?", "&") }
    };
  }

  // Redirect to set password page
  const isPasswordResetRequired = to.name !== "SetPassword" && store.isPasswordResetRequired;
  if (isPasswordResetRequired && !store.isActivationRequired && store.acceptedTerms) {
    return {
      path: "/set-password"
    };
  }

  // Check if there is a redirect query parameter and redirect to that path
  const isRedirect =
    store.isAuthenticated &&
    store.acceptedTerms &&
    !store.isPasswordResetRequired &&
    !store.isActivationRequired &&
    to.query.redirect &&
    to.name !== "UserShippingAddress" &&
    to.name !== "SetPassword" &&
    to.path !== to.query.redirect;
  if (isRedirect) {
    return {
      path: to.query.redirect
    };
  }
});

export default router;
